import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { OAuthResponseType, OktaAuth, Tokens as OktaTokens } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import type { RenderError, RenderResult, WidgetOptions } from '@okta/okta-signin-widget';
import OktaSignIn from '@okta/okta-signin-widget';

import { logger } from '../../features/logger';
import authenticateWithDxpSso, { isSafari } from '../../utils/auth/dxp-sso';
import type { Tokens as LocalTokens } from '../../utils/okta';
import { transformTokenForCookies } from '../../utils/okta';
import SplashLoader from '../SplashLoader';

import '../../../node_modules/@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
// NOTE: This was copied from platform-fe; it is not ideal to duplicate these css files, but adding
// a package.json dependency back to platform-fe would cause a circular dependency.
// Given the login component styling changes infrequently, this seems like a low risk solution.
// Eventually, platform-fe will likely absorb this repository, which will eleminate the duplicated css files.
import './login-component.css';

export interface OktaWidgetConfig {
  baseUrl?: string;
  clientId?: string;
  issuer: string;
  pkce?: boolean;
  redirectUri?: string;
  responseType?: OAuthResponseType[];
  samlUri?: string;
  samlFromUri?: string;
  scopes?: string[];
}

export interface LoginComponentProps {
  i18n: Record<string, unknown>;
  oktaConfig: OktaWidgetConfig;
  language?: string;
  afterRenderCallback?: ((controller: string | null) => void) | null;
  customButton?: ReactNode;
}

export const buildWidgetOptions = (
  i18n: Record<string, unknown>,
  language: string,
  oktaConfig: OktaWidgetConfig
): WidgetOptions => {
  const { baseUrl, clientId, issuer, pkce, redirectUri, responseType, scopes } = oktaConfig;
  // Returning expression immediately to make Sonar happy
  return {
    authParams: {
      issuer,
      scopes,
      pkce,
      responseType
    },
    language,
    baseUrl,
    clientId,
    i18n,
    redirectUri,
    useClassicEngine: true
  };
};

export const renderWidget = (
  widget: OktaSignIn,
  widgetRef: RefObject<HTMLDivElement | null>,
  oktaAuth: OktaAuth
): void => {
  widget.renderEl(
    {
      // Okta's published types declare `el: string`, but renderEl accepts a DOM
      // element at runtime. Remove this cast once @okta/okta-signin-widget types
      // are fixed.
      el: widgetRef.current as unknown as string
    },
    (res: RenderResult) => {
      logger.info(`Red-Carpet-FE- Login Successful`);
      const tokens = 'tokens' in res ? res.tokens : undefined;
      // transformTokenForCookies uses the local Tokens shape; okta-auth-js Tokens
      // differ structurally but are compatible at runtime.
      oktaAuth.handleLoginRedirect(transformTokenForCookies(tokens as unknown as LocalTokens) as unknown as OktaTokens);
    },
    (err: RenderError) => {
      throw err;
    }
  );
};

const onAuthenticated = async (oktaAuth: OktaAuth): Promise<void> => {
  const originalUri = oktaAuth.getOriginalUri();
  oktaAuth.removeOriginalUri();
  const { restoreOriginalUri } = oktaAuth.options;
  await restoreOriginalUri?.(oktaAuth, originalUri ?? '');
};

const LoginComponent = ({
  afterRenderCallback = null,
  i18n,
  language = 'en',
  oktaConfig,
  customButton
}: LoginComponentProps) => {
  const { t } = useTranslation();
  const { oktaAuth } = useOktaAuth();
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Safari cannot reach the cross-site Okta session (ITP blocks the third-party
    // cookie), so the silent-auth flow used for every other browser always fails
    // with an INACTIVE session. Take a dedicated Safari path that adopts the DXP
    // platform's shared, first-party SSO tokens. Chrome/Edge/Firefox keep the
    // original flow below completely untouched.
    if (isSafari()) {
      let isActive = true;

      (async () => {
        if (await oktaAuth.isAuthenticated()) {
          onAuthenticated(oktaAuth);
          return;
        }

        if (isActive && authenticateWithDxpSso(oktaAuth)) {
          onAuthenticated(oktaAuth);
        }
      })();

      return () => {
        isActive = false;
      };
    }

    // if the user is already authenticated rerun restoreOriginalUri so the user is redirected to the proper url
    (async () => {
      if (await oktaAuth.isAuthenticated()) {
        onAuthenticated(oktaAuth);
      }
    })();

    if (!widgetRef.current) {
      return; // Do not render the sign-in widget if user is already authenticated; prevents screen flash
    }

    const widgetOptions = buildWidgetOptions(i18n, language, oktaConfig);
    const widget = new OktaSignIn(widgetOptions);

    // saml login
    oktaAuth.session.get().then(session => {
      if (session.status.toLowerCase() === 'active') {
        oktaAuth.token.getWithoutPrompt(oktaConfig).then(({ tokens }) => {
          // okta-auth-js Tokens differ structurally from the local Tokens shape
          // but are compatible at runtime.
          oktaAuth.tokenManager.setTokens(
            transformTokenForCookies(tokens as unknown as LocalTokens) as unknown as OktaTokens
          );
          onAuthenticated(oktaAuth);
        });
      }
    });

    if (afterRenderCallback) {
      widget.on('afterRender', (context: { controller: string | null }) => afterRenderCallback(context.controller));
    }

    return () => widget.remove();
  }, [afterRenderCallback, i18n, language, oktaAuth, oktaConfig, customButton, t]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}
    >
      <SplashLoader />
      <div ref={widgetRef} />
    </div>
  );
};

export default LoginComponent;
